<template>
  <div
    class="min-h-full bg-slate-50/60 dark:bg-[#0B1120] text-slate-900 dark:text-[#F1F5F9] pb-16 transition-colors"
  >
    <div class="w-full lg:max-w-[1140px] xl:max-w-[1260px] mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 space-y-4 sm:space-y-6">
      <!-- ==================== HEADER SECTION ==================== -->
      <div class="space-y-3.5">
        <div
          class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center shrink-0 shadow-xs"
            >
              <Megaphone class="w-5 h-5" />
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
                  Faculty Announcements
                </h1>
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/70 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 shadow-xs"
                >
                  <Users class="w-3 h-3" />
                  Teachers Only
                </span>
              </div>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5">
                Official school broadcasts and private direct communications for faculty staff
              </p>
            </div>
          </div>

          <!-- New Announcement Button (Admin Only) -->
          <div v-if="isAdmin" class="flex items-center gap-2">
            <button
              @click="openCreateForm"
              class="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-98 rounded-xl transition-all shadow-sm shadow-indigo-600/20"
            >
              <Plus class="w-4 h-4" />
              <span>New Announcement</span>
            </button>
          </div>
        </div>

        <!-- Audience Info Banner -->
        <div
          class="p-2.5 sm:p-3 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-blue-50/40 dark:bg-[#111C30]/80 flex items-center justify-between gap-2.5 transition-colors duration-200 text-xs"
        >
          <div class="flex items-center gap-2 text-slate-700 dark:text-[#A6B4C8]">
            <Info class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <span>
              <strong class="font-bold text-slate-900 dark:text-[#F1F5F9]">Faculty Restricted:</strong>
              Visible exclusively to teachers and administrators. Students and parents do not receive these notices.
            </span>
          </div>
          <span
            class="hidden md:inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-[#7E8DA5] shrink-0"
          >
            <Mail class="w-3 h-3 text-emerald-500" />
            Email delivery supported
          </span>
        </div>

        <!-- Admin Quick Statistics (Matching Student design system) -->
        <div
          v-if="isAdmin && announcements.length"
          class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4"
        >
          <!-- Total Notices -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
          >
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <div class="p-1.5 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600 dark:text-blue-400">
                  <Megaphone class="w-4 h-4" />
                </div>
                <span class="text-[10px] font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#A6B4C8] px-1.5 py-0.5 rounded">All</span>
              </div>
              <h3 class="text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">Total Notices</h3>
              <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">{{ announcements.length }}</p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div class="bg-blue-500 h-full rounded-full" style="width: 100%"></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1">Broadcasts and direct</p>
            </div>
          </div>

          <!-- All Teachers -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
          >
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <div class="p-1.5 bg-cyan-50 dark:bg-cyan-950/40 rounded-lg text-cyan-600 dark:text-cyan-400">
                  <Users class="w-4 h-4" />
                </div>
                <span class="text-[10px] font-semibold bg-cyan-50 dark:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400 px-1.5 py-0.5 rounded">Broadcast</span>
              </div>
              <h3 class="text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">All Teachers</h3>
              <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">{{ broadcastCount }}</p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div class="bg-cyan-500 h-full rounded-full transition-all duration-500" :style="{ width: `${announcements.length ? (broadcastCount / announcements.length) * 100 : 0}%` }"></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1">School-wide notices</p>
            </div>
          </div>

          <!-- Direct Messages -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
          >
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <div class="p-1.5 bg-purple-50 dark:bg-purple-950/40 rounded-lg text-purple-600 dark:text-purple-400">
                  <UserCheck class="w-4 h-4" />
                </div>
                <span class="text-[10px] font-semibold bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded">Private</span>
              </div>
              <h3 class="text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">Direct Messages</h3>
              <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">{{ directCount }}</p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div class="bg-purple-500 h-full rounded-full transition-all duration-500" :style="{ width: `${announcements.length ? (directCount / announcements.length) * 100 : 0}%` }"></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1">Targeted faculty alerts</p>
            </div>
          </div>

          <!-- Pinned -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
          >
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <div class="p-1.5 bg-amber-50 dark:bg-amber-950/40 rounded-lg text-amber-600 dark:text-amber-400">
                  <Pin class="w-4 h-4" />
                </div>
                <span class="text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded">Priority</span>
              </div>
              <h3 class="text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">Pinned</h3>
              <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">{{ pinnedCount }}</p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div class="bg-amber-500 h-full rounded-full transition-all duration-500" :style="{ width: `${announcements.length ? (pinnedCount / announcements.length) * 100 : 0}%` }"></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1">Highlighted announcements</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== SEARCH & FILTER TOOLBAR ==================== -->
      <div class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] p-3 sm:p-3.5 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <!-- Filter Tabs -->
        <div class="grid grid-cols-2 gap-1 sm:flex sm:items-center p-1 bg-slate-100/90 dark:bg-[#18243A] rounded-xl overflow-x-auto">
          <button
            v-for="tab in filterTabs"
            :key="tab.id"
            @click="activeFilter = tab.id"
            class="px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 whitespace-nowrap"
            :class="[
              activeFilter === tab.id
                ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/80 dark:border-[#263449]'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
            ]"
          >
            <component :is="tab.icon" class="w-3.5 h-3.5" />
            <span>{{ tab.label }}</span>
            <span
              class="px-1.5 py-0.2 rounded-full text-[10px] font-bold"
              :class="activeFilter === tab.id ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400' : 'bg-slate-200/80 dark:bg-[#263449] text-slate-500 dark:text-[#7E8DA5]'"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="relative w-full sm:w-60 md:w-72 shrink-0">
          <Search class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search announcements..."
            class="w-full pl-9 pr-8 py-2 bg-slate-50 dark:bg-[#18243A] border border-slate-200/90 dark:border-[#263449] rounded-xl text-xs text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
          />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <!-- ==================== LOADING SKELETON ==================== -->
      <div v-if="loading" class="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div
          v-for="i in 4"
          :key="i"
          class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-4.5 sm:p-5 animate-pulse flex flex-col justify-between h-60 space-y-3 shadow-xs"
        >
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <div class="h-4.5 bg-slate-200 dark:bg-[#18243A] rounded-full w-20"></div>
              <div class="h-4.5 bg-slate-200 dark:bg-[#18243A] rounded-full w-16"></div>
            </div>
            <div class="h-5 bg-slate-200 dark:bg-[#18243A] rounded w-2/3"></div>
            <div class="h-3.5 bg-slate-200 dark:bg-[#18243A] rounded w-full"></div>
            <div class="h-3.5 bg-slate-200 dark:bg-[#18243A] rounded w-4/5"></div>
          </div>
          <div class="pt-3 border-t border-slate-100 dark:border-[#263449] flex justify-between items-center">
            <div class="h-7 bg-slate-200 dark:bg-[#18243A] rounded w-24"></div>
            <div class="h-7 bg-slate-200 dark:bg-[#18243A] rounded w-16"></div>
          </div>
        </div>
      </div>

      <!-- ==================== ANNOUNCEMENTS LIST ==================== -->
      <div v-else-if="displayAnnouncements.length > 0" class="w-full">
        <transition-group
          name="list"
          tag="div"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          <div
            v-for="announcement in displayAnnouncements"
            :key="announcement._id"
            class="group rounded-2xl border transition-all duration-200 p-4 sm:p-5 flex flex-col justify-between h-full bg-white dark:bg-[#111C30] relative overflow-hidden shadow-xs hover:border-slate-300 dark:hover:border-slate-700"
            :class="[
              announcement.isPinned
                ? 'border-amber-300/80 dark:border-amber-700/60'
                : isDirectToCurrentUser(announcement)
                ? 'border-purple-300/80 dark:border-purple-700/60'
                : 'border-slate-200/90 dark:border-[#263449]',
            ]"
          >
            <!-- Top Status Accent Line (Signature Design) -->
            <div
              class="absolute top-0 left-0 right-0 h-1"
              :class="[
                announcement.isPinned
                  ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                  : isDirectToCurrentUser(announcement)
                  ? 'bg-gradient-to-r from-purple-400 to-indigo-500'
                  : 'bg-gradient-to-r from-blue-400 to-cyan-500',
              ]"
            ></div>

            <div class="flex-1 flex flex-col pt-1">
              <!-- Top Badges & Meta Row -->
              <div class="flex flex-wrap items-center justify-between gap-1.5 mb-2.5">
                <div class="flex flex-wrap items-center gap-1.5">
                  <!-- Pinned Badge -->
                  <span
                    v-if="announcement.isPinned"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 shadow-xs"
                  >
                    <Pin class="w-2.5 h-2.5" />
                    PINNED
                  </span>

                  <!-- Target Audience Badge -->
                  <span
                    v-if="isDirectToCurrentUser(announcement)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold bg-purple-50 text-purple-700 dark:bg-purple-950/80 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60 shadow-xs"
                  >
                    <UserCheck class="w-2.5 h-2.5" />
                    Direct to You
                  </span>

                  <span
                    v-else-if="announcement.targetType === 'specific_teacher'"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-purple-50 text-purple-700 dark:bg-purple-950/80 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60 max-w-[140px] truncate shadow-xs"
                    :title="`Direct: ${getTargetTeacherName(announcement)}`"
                  >
                    <UserCheck class="w-2.5 h-2.5 shrink-0" />
                    <span class="truncate">Direct: {{ getTargetTeacherName(announcement) }}</span>
                  </span>

                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40 shadow-xs"
                  >
                    <Users class="w-2.5 h-2.5" />
                    All Teachers
                  </span>

                  <!-- Email Sent Badge -->
                  <span
                    v-if="announcement.emailSent"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40 shadow-xs"
                    :title="`Email delivered to ${announcement.emailRecipientsCount || 1} teacher(s)`"
                  >
                    <Mail class="w-2.5 h-2.5" />
                    Delivered ({{ announcement.emailRecipientsCount || 1 }})
                  </span>

                  <!-- Expiry Badge -->
                  <span
                    v-if="announcement.expiryDate"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-medium bg-slate-100 text-slate-600 dark:bg-[#18243A] dark:text-[#A6B4C8]"
                  >
                    <Clock class="w-2.5 h-2.5" />
                    Expires {{ formatDate(announcement.expiryDate) }}
                  </span>
                </div>

                <!-- Timestamp -->
                <p class="text-[10px] sm:text-[11px] text-slate-400 dark:text-[#7E8DA5] whitespace-nowrap">
                  {{ formatDate(announcement.createdAt) }}
                </p>
              </div>

              <!-- Title -->
              <h3 class="text-sm sm:text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1 leading-snug break-words">
                {{ announcement.title }}
              </h3>

              <!-- Author / Recipient Subtitle -->
              <p class="text-[11px] sm:text-xs text-slate-500 dark:text-[#A6B4C8] mb-2.5 flex items-center gap-1.5 flex-wrap">
                <span>By <strong class="text-slate-700 dark:text-[#F1F5F9]">{{ announcement.createdBy?.name || "School Admin" }}</strong></span>
                <span>•</span>
                <span v-if="announcement.targetType === 'specific_teacher'">
                  Target: <strong class="text-purple-600 dark:text-purple-400">{{ getTargetTeacherName(announcement) }}</strong>
                </span>
                <span v-else>Audience: <strong>All Teachers</strong></span>
              </p>

              <!-- Message Body -->
              <div
                class="text-slate-600 dark:text-[#CBD5E1] text-xs sm:text-sm leading-relaxed mb-4 whitespace-pre-wrap break-words flex-1"
              >
                {{ announcement.message }}
              </div>
            </div>

            <!-- Actions & Read Status Footer -->
            <div class="flex items-center justify-between border-t border-slate-100 dark:border-[#263449] pt-3 mt-auto flex-wrap gap-2">
              <!-- Teacher Read Status -->
              <div class="flex items-center gap-2">
                <button
                  v-if="!isAnnouncementRead(announcement._id)"
                  @click="markAsRead(announcement._id)"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white text-xs font-semibold rounded-xl transition-all shadow-xs"
                >
                  <Check class="w-3.5 h-3.5" />
                  Mark as Read
                </button>
                <span
                  v-else
                  class="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 text-xs font-medium rounded-xl border border-emerald-200 dark:border-emerald-800/40"
                >
                  <CheckCircle2 class="w-3.5 h-3.5" />
                  Read
                </span>

                <!-- Read Counter for Admin -->
                <span
                  v-if="isAdmin"
                  class="text-xs text-slate-400 dark:text-[#7E8DA5] font-medium"
                >
                  👁️ {{ announcement.readBy?.length || 0 }} read
                </span>
              </div>

              <!-- Admin Edit & Delete Actions -->
              <div v-if="isAdmin" class="flex items-center gap-1.5">
                <button
                  @click="editAnnouncement(announcement)"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#1f2f4c] text-slate-700 dark:text-[#F1F5F9] text-xs font-semibold rounded-xl border border-slate-200 dark:border-[#263449] transition-all shadow-xs"
                >
                  <Edit3 class="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  @click="removeAnnouncement(announcement._id)"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-950/70 text-red-600 dark:text-red-400 text-xs font-semibold rounded-xl border border-red-200/80 dark:border-red-900/40 transition-all shadow-xs"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- ==================== EMPTY STATE ==================== -->
      <div
        v-else
        class="max-w-xl mx-auto w-full bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] p-8 sm:p-12 text-center"
      >
        <div class="w-14 h-14 mx-auto mb-3 rounded-2xl bg-blue-50 dark:bg-[#18243A] text-blue-600 dark:text-blue-400 flex items-center justify-center">
          <Megaphone class="w-7 h-7" />
        </div>
        <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
          {{ searchQuery ? "No Announcements Match Your Search" : "No Teacher Announcements Yet" }}
        </h3>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] max-w-sm mx-auto mb-4">
          {{
            searchQuery
              ? "Try clearing your search query or switching tabs."
              : isAdmin
              ? "Click 'New Announcement' above to publish a message for teachers."
              : "There are currently no active announcements for teaching staff."
          }}
        </p>
        <div v-if="searchQuery">
          <button
            @click="searchQuery = ''; activeFilter = 'all'"
            class="px-3.5 py-1.5 bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#F1F5F9] rounded-xl text-xs font-semibold border border-slate-200/80 dark:border-[#263449]"
          >
            Clear Search
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== POPUP MODAL (TOP/CENTERED, FULLY RESPONSIVE) ==================== -->
    <teleport to="body">
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showCreateForm && isAdmin"
          class="fixed inset-0 z-[1200] overflow-y-auto bg-slate-950/75 dark:bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-2.5 sm:p-4 md:p-6"
          @click.self="closeForm"
        >
          <transition
            enter-active-class="transition duration-250 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 -translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              v-if="showCreateForm"
              class="relative w-full max-w-lg md:max-w-xl bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl shadow-2xl overflow-hidden my-3 sm:my-auto transition-all flex flex-col max-h-[92vh]"
              @click.stop
            >
              <!-- Modal Sticky Header -->
              <div
                class="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#0F172A]/90 sticky top-0 z-20 backdrop-blur-md flex-shrink-0"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0"
                  >
                    <Megaphone class="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">
                      {{ editingId ? "Edit Announcement" : "New Faculty Announcement" }}
                    </h2>
                    <p class="text-xs text-slate-500 dark:text-[#7E8DA5]">
                      Deliver broadcast or targeted notices to teachers
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  @click="closeForm"
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-[#F1F5F9] hover:bg-slate-200/60 dark:hover:bg-[#18243A] transition-colors"
                  title="Close (Esc)"
                >
                  <X class="w-4.5 h-4.5" />
                </button>
              </div>

              <!-- Modal Scrollable Body -->
              <form
                id="announcementForm"
                @submit.prevent="submitForm"
                class="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1 scrollbar-thin"
              >
                <!-- TARGET SELECTION (ALL TEACHERS vs A PARTICULAR TEACHER) -->
                <div>
                  <label class="block text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9] mb-2">
                    Target Audience <span class="text-red-500">*</span>
                  </label>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <!-- Option 1: All Teachers -->
                    <button
                      type="button"
                      @click="formData.targetType = 'all_teachers'; formData.targetTeacher = ''"
                      class="relative p-3 rounded-xl border text-left flex items-start gap-3 transition-all duration-150"
                      :class="[
                        formData.targetType === 'all_teachers'
                          ? 'bg-blue-50/90 dark:bg-[#18243A] border-blue-500 ring-2 ring-blue-500/20'
                          : 'bg-white dark:bg-[#172235]/60 border-slate-200 dark:border-[#263449] hover:border-slate-300 dark:hover:border-slate-600',
                      ]"
                    >
                      <div
                        class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        :class="[
                          formData.targetType === 'all_teachers'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#A6B4C8]',
                        ]"
                      >
                        <Users class="w-4 h-4" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between">
                          <span class="font-bold text-xs sm:text-sm text-slate-900 dark:text-[#F1F5F9]">
                            All Teachers
                          </span>
                          <span
                            v-if="formData.targetType === 'all_teachers'"
                            class="text-[10px] font-semibold px-1.5 py-0.2 rounded-full bg-indigo-600 text-white"
                          >
                            Active
                          </span>
                        </div>
                        <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] mt-0.5">
                          Broadcast to all registered faculty members.
                        </p>
                      </div>
                    </button>

                    <!-- Option 2: A Particular Teacher -->
                    <button
                      type="button"
                      @click="formData.targetType = 'specific_teacher'"
                      class="relative p-3 rounded-xl border text-left flex items-start gap-3 transition-all duration-150"
                      :class="[
                        formData.targetType === 'specific_teacher'
                          ? 'bg-purple-50/90 dark:bg-[#18243A] border-purple-500 ring-2 ring-purple-500/20'
                          : 'bg-white dark:bg-[#172235]/60 border-slate-200 dark:border-[#263449] hover:border-slate-300 dark:hover:border-slate-600',
                      ]"
                    >
                      <div
                        class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        :class="[
                          formData.targetType === 'specific_teacher'
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#A6B4C8]',
                        ]"
                      >
                        <UserCheck class="w-4 h-4" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between">
                          <span class="font-bold text-xs sm:text-sm text-slate-900 dark:text-[#F1F5F9]">
                            A Particular Teacher
                          </span>
                          <span
                            v-if="formData.targetType === 'specific_teacher'"
                            class="text-[10px] font-semibold px-1.5 py-0.2 rounded-full bg-purple-600 text-white"
                          >
                            Active
                          </span>
                        </div>
                        <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] mt-0.5">
                          Private notice for a single selected teacher.
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- TEACHER DROPDOWN (ANIMATED) -->
                <transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="opacity-0 -translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 -translate-y-2"
                >
                  <div
                    v-if="formData.targetType === 'specific_teacher'"
                    class="p-3.5 rounded-xl border bg-purple-50/40 dark:bg-[#18243A]/60 border-purple-200 dark:border-purple-900/40 space-y-2.5"
                  >
                    <div class="flex items-center justify-between">
                      <label class="block text-xs font-semibold text-slate-900 dark:text-[#F1F5F9]">
                        Select Teacher <span class="text-red-500">*</span>
                      </label>
                      <span class="text-[11px] text-purple-700 dark:text-purple-300 font-medium">
                        {{ teachersList.length }} teachers available
                      </span>
                    </div>

                    <div class="relative">
                      <select
                        v-model="formData.targetTeacher"
                        required
                        class="w-full px-3.5 py-2.5 bg-white dark:bg-[#172235] border border-slate-300 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/25 transition-all text-xs sm:text-sm appearance-none pr-9 cursor-pointer"
                      >
                        <option value="" disabled>-- Select a teacher from your school --</option>
                        <option
                          v-for="teacher in teachersList"
                          :key="teacher._id || teacher.id"
                          :value="teacher._id || teacher.id"
                        >
                          {{ teacher.name }} ({{ teacher.email || 'No email' }}) {{ teacher.subject ? '— ' + teacher.subject : '' }}
                        </option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
                        <ChevronDown class="w-4 h-4" />
                      </div>
                    </div>

                    <!-- Selected Teacher Preview -->
                    <div
                      v-if="selectedTeacherInfo"
                      class="flex items-center gap-2.5 p-2 rounded-lg bg-white dark:bg-[#111C30] border border-purple-100 dark:border-purple-900/50 text-xs"
                    >
                      <div class="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex items-center justify-center font-bold text-[10px] uppercase flex-shrink-0">
                        {{ selectedTeacherInfo.name ? selectedTeacherInfo.name.charAt(0) : 'T' }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="font-semibold text-slate-900 dark:text-[#F1F5F9] truncate text-xs">
                          {{ selectedTeacherInfo.name }}
                        </p>
                        <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] truncate">
                          {{ selectedTeacherInfo.email }} • {{ selectedTeacherInfo.subject || 'Faculty' }}
                        </p>
                      </div>
                      <span class="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-950/70 dark:text-purple-300 whitespace-nowrap">
                        Direct
                      </span>
                    </div>
                  </div>
                </transition>

                <!-- TITLE -->
                <div>
                  <label class="block text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9] mb-1.5">
                    Announcement Title <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="formData.title"
                    type="text"
                    placeholder="e.g. Staff Meeting on Friday, Syllabus Updates..."
                    class="w-full px-3.5 py-2.5 bg-white dark:bg-[#172235] border border-slate-300 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/25 transition-all text-xs sm:text-sm"
                    required
                  />
                </div>

                <!-- MESSAGE -->
                <div>
                  <div class="flex items-center justify-between mb-1.5">
                    <label class="block text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9]">
                      Message Content <span class="text-red-500">*</span>
                    </label>
                    <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
                      {{ formData.message.length }} chars
                    </span>
                  </div>
                  <textarea
                    v-model="formData.message"
                    placeholder="Write your announcement details here..."
                    rows="4"
                    class="w-full px-3.5 py-2.5 bg-white dark:bg-[#172235] border border-slate-300 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/25 transition-all text-xs sm:text-sm resize-y"
                    required
                  ></textarea>
                </div>

                <!-- EMAIL NOTIFICATION SWITCH -->
                <div
                  class="p-3.5 rounded-xl border transition-all duration-150"
                  :class="[
                    formData.sendEmail
                      ? 'bg-emerald-50/80 dark:bg-emerald-950/20 border-emerald-400 dark:border-emerald-800/60 ring-1 ring-emerald-500/20'
                      : 'bg-slate-50/70 dark:bg-[#18243A]/50 border-slate-200 dark:border-[#263449]',
                  ]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex items-start gap-2.5">
                      <div
                        class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                        :class="[
                          formData.sendEmail
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-200 dark:bg-[#172235] text-slate-500 dark:text-[#A6B4C8]',
                        ]"
                      >
                        <Mail class="w-4 h-4" />
                      </div>
                      <div>
                        <div class="flex items-center gap-2">
                          <h4 class="font-bold text-xs sm:text-sm text-slate-900 dark:text-[#F1F5F9]">
                            Send Email Notification
                          </h4>
                          <span
                            v-if="formData.sendEmail"
                            class="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-emerald-600 text-white uppercase"
                          >
                            Active
                          </span>
                        </div>
                        <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] mt-0.5">
                          Sends an HTML copy directly to teacher inboxes.
                        </p>

                        <!-- Dynamic Recipient Preview -->
                        <div class="mt-1.5 text-xs font-medium">
                          <span
                            v-if="formData.sendEmail && formData.targetType === 'all_teachers'"
                            class="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-900/40 px-2 py-0.5 rounded-md text-[11px]"
                          >
                            <Users class="w-3 h-3" />
                            Email to all {{ teachersList.length }} teachers
                          </span>
                          <span
                            v-else-if="formData.sendEmail && formData.targetType === 'specific_teacher' && selectedTeacherInfo"
                            class="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-900/40 px-2 py-0.5 rounded-md text-[11px]"
                          >
                            <Mail class="w-3 h-3" />
                            Email to: {{ selectedTeacherInfo.name }} &lt;{{ selectedTeacherInfo.email }}&gt;
                          </span>
                          <span
                            v-else-if="formData.sendEmail && formData.targetType === 'specific_teacher' && !selectedTeacherInfo"
                            class="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-900/40 px-2 py-0.5 rounded-md text-[11px]"
                          >
                            ⚠️ Select a teacher to email
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Switch -->
                    <label class="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        v-model="formData.sendEmail"
                        class="sr-only peer"
                      />
                      <div
                        class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-[#172235] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"
                      ></div>
                    </label>
                  </div>
                </div>

                <!-- PINNED & EXPIRY ROW -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <!-- Pinned Toggle -->
                  <div
                    class="p-3 rounded-xl border bg-slate-50/70 dark:bg-[#18243A]/50 border-slate-200 dark:border-[#263449] flex items-center justify-between"
                  >
                    <div class="flex items-center gap-2">
                      <div class="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                        <Pin class="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <label for="pinned" class="text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9] cursor-pointer">
                          Pin to Top
                        </label>
                        <p class="text-[11px] text-slate-500 dark:text-[#7E8DA5]">
                          High priority placement
                        </p>
                      </div>
                    </div>
                    <input
                      id="pinned"
                      v-model="formData.isPinned"
                      type="checkbox"
                      class="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </div>

                  <!-- Expiry Date -->
                  <div
                    class="p-3 rounded-xl border bg-slate-50/70 dark:bg-[#18243A]/50 border-slate-200 dark:border-[#263449]"
                  >
                    <div class="flex items-center gap-1.5 mb-1">
                      <Clock class="w-3 h-3 text-slate-500 dark:text-[#7E8DA5]" />
                      <label class="text-xs font-semibold text-slate-800 dark:text-[#F1F5F9]">
                        Expiry Date (Optional)
                      </label>
                    </div>
                    <input
                      v-model="formData.expiryDate"
                      type="datetime-local"
                      class="w-full px-2.5 py-1.5 bg-white dark:bg-[#172235] border border-slate-300 dark:border-[#263449] rounded-lg text-xs text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </form>

              <!-- Modal Sticky Footer Bar -->
              <div
                class="flex items-center justify-end gap-2.5 px-5 sm:px-6 py-3.5 border-t border-slate-100 dark:border-[#263449] bg-slate-50/90 dark:bg-[#0F172A]/90 sticky bottom-0 z-20 backdrop-blur-md flex-shrink-0"
              >
                <button
                  type="button"
                  @click="closeForm"
                  class="px-4 py-2 bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235] font-semibold rounded-xl border border-slate-200 dark:border-[#263449] transition-all text-xs sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="announcementForm"
                  :disabled="isSubmitting || (formData.targetType === 'specific_teacher' && !formData.targetTeacher)"
                  class="inline-flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-md shadow-indigo-600/20 text-xs sm:text-sm"
                >
                  <AppLoader v-if="isSubmitting" variant="button" class="w-3.5 h-3.5 text-white" />
                  <component :is="editingId ? Edit3 : Send" class="w-3.5 h-3.5" v-else />
                  {{
                    isSubmitting
                      ? "Publishing..."
                      : editingId
                      ? "Update Announcement"
                      : "Publish Announcement"
                  }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import {
    Megaphone,
    Users,
    UserCheck,
    Mail,
    Pin,
    Clock,
    Search,
    Plus,
    X,
    Send,
    Edit3,
    Trash2,
    Check,
    CheckCircle2,
    ChevronDown,
    Info,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const store = useStore();
  const { showToast } = useGlobalToast();

  /* ---------------- FORM & MODAL STATE ---------------- */
  const showCreateForm = ref(false);
  const isSubmitting = ref(false);
  const editingId = ref(null);
  const searchQuery = ref("");
  const activeFilter = ref("all");

  const formData = ref({
    title: "",
    message: "",
    targetType: "all_teachers",
    targetTeacher: "",
    expiryDate: "",
    isPinned: false,
    sendEmail: false,
  });

  /* ---------------- STORE STATE ---------------- */
  const announcements = computed(() => store.state.announcements || []);
  const loading = computed(() => store.state.announcementsLoading);
  const userRole = computed(() => store.state.userRole || store.getters.getUserRole);
  const currentUserId = computed(() => store.state.userId || store.getters.getUserId);
  const teachersList = computed(() => store.getters.getAllTeachers || []);

  const isAdmin = computed(() => userRole.value === "admin");

  /* ---------------- COMPUTED HELPERS ---------------- */
  const selectedTeacherInfo = computed(() => {
    if (!formData.value.targetTeacher) return null;
    return teachersList.value.find(
      (t) => String(t._id || t.id) === String(formData.value.targetTeacher)
    );
  });

  const broadcastCount = computed(() => {
    return announcements.value.filter((a) => a.targetType !== "specific_teacher").length;
  });

  const directCount = computed(() => {
    return announcements.value.filter((a) => a.targetType === "specific_teacher").length;
  });

  const pinnedCount = computed(() => {
    return announcements.value.filter((a) => a.isPinned).length;
  });

  const filterTabs = computed(() => [
    { id: "all", label: "All Notices", count: announcements.value.length, icon: Megaphone },
    { id: "broadcast", label: "All Teachers", count: broadcastCount.value, icon: Users },
    { id: "direct", label: "Direct", count: directCount.value, icon: UserCheck },
    { id: "pinned", label: "Pinned", count: pinnedCount.value, icon: Pin },
  ]);

  /* ---------------- FILTER & SEARCH ---------------- */
  const displayAnnouncements = computed(() => {
    if (!Array.isArray(announcements.value)) return [];

    let list = announcements.value.slice();

    if (activeFilter.value === "broadcast") {
      list = list.filter((a) => a.targetType !== "specific_teacher");
    } else if (activeFilter.value === "direct") {
      list = list.filter((a) => a.targetType === "specific_teacher");
    } else if (activeFilter.value === "pinned") {
      list = list.filter((a) => a.isPinned);
    }

    if (searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase().trim();
      list = list.filter(
        (a) =>
          a.title?.toLowerCase().includes(q) ||
          a.message?.toLowerCase().includes(q) ||
          a.targetTeacher?.name?.toLowerCase().includes(q)
      );
    }

    return list.sort((a, b) => {
      if (a?.isPinned && !b?.isPinned) return -1;
      if (!a?.isPinned && b?.isPinned) return 1;
      return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
    });
  });

  /* ---------------- TARGET TEACHER HELPERS ---------------- */
  function getTargetTeacherName(announcement) {
    if (announcement.targetTeacher && typeof announcement.targetTeacher === "object") {
      return (
        announcement.targetTeacher.name ||
        announcement.targetTeacher.email ||
        "Specific Teacher"
      );
    }
    if (announcement.targetTeacher) {
      const found = teachersList.value.find(
        (t) => String(t._id || t.id) === String(announcement.targetTeacher)
      );
      if (found) return found.name;
    }
    return "Specific Teacher";
  }

  function isDirectToCurrentUser(announcement) {
    if (announcement.targetType !== "specific_teacher") return false;
    const teacherId =
      announcement.targetTeacher?._id ||
      announcement.targetTeacher?.id ||
      announcement.targetTeacher;
    return teacherId && String(teacherId) === String(currentUserId.value);
  }

  /* ---------------- MODAL CONTROLS ---------------- */
  function openCreateForm() {
    resetForm();
    showCreateForm.value = true;
  }

  function closeForm() {
    showCreateForm.value = false;
    resetForm();
  }

  function resetForm() {
    formData.value = {
      title: "",
      message: "",
      targetType: "all_teachers",
      targetTeacher: "",
      expiryDate: "",
      isPinned: false,
      sendEmail: false,
    };
    editingId.value = null;
  }

  watch(showCreateForm, (isOpen) => {
    if (typeof document !== "undefined") {
      document.body.style.overflow = isOpen ? "hidden" : "";
    }
  });

  function handleKeyDown(e) {
    if (e.key === "Escape" && showCreateForm.value) {
      closeForm();
    }
  }

  /* ---------------- ACTIONS ---------------- */
  async function submitForm() {
    if (formData.value.targetType === "specific_teacher" && !formData.value.targetTeacher) {
      alert("Please select a specific teacher for this direct announcement.");
      return;
    }

    isSubmitting.value = true;

    try {
      const payload = {
        title: formData.value.title.trim(),
        message: formData.value.message.trim(),
        targetType: formData.value.targetType,
        targetTeacher:
          formData.value.targetType === "specific_teacher"
            ? formData.value.targetTeacher
            : null,
        targetRole: "teacher",
        isPinned: !!formData.value.isPinned,
        sendEmail: !!formData.value.sendEmail,
        expiryDate: formData.value.expiryDate
          ? new Date(formData.value.expiryDate).toISOString()
          : null,
      };

      if (editingId.value) {
        await store.dispatch("updateAnnouncement", {
          id: editingId.value,
          data: payload,
        });
        showToast(
          "success",
          formData.value.sendEmail
            ? "Announcement updated & email notification sent!"
            : "Announcement updated successfully!"
        );
      } else {
        await store.dispatch("createAnnouncement", payload);
        showToast(
          "success",
          formData.value.sendEmail
            ? "Announcement published & email notifications dispatched!"
            : "Announcement published successfully for teachers!"
        );
      }

      closeForm();
      await store.dispatch("fetchAnnouncements");
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.error || "Failed to save announcement";
      alert(errMsg);
    } finally {
      isSubmitting.value = false;
    }
  }

  function editAnnouncement(announcement) {
    formData.value = {
      title: announcement.title || "",
      message: announcement.message || "",
      targetType: announcement.targetType || "all_teachers",
      targetTeacher:
        announcement.targetTeacher?._id ||
        announcement.targetTeacher?.id ||
        announcement.targetTeacher ||
        "",
      expiryDate: announcement.expiryDate
        ? announcement.expiryDate.slice(0, 16)
        : "",
      isPinned: !!announcement.isPinned,
      sendEmail: false,
    };

    editingId.value = announcement._id;
    showCreateForm.value = true;
  }

  async function removeAnnouncement(id) {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await store.dispatch("deleteAnnouncement", id);
      showToast("success", "Announcement deleted successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to delete announcement");
    }
  }

  async function markAsRead(id) {
    await store.dispatch("markAnnouncementRead", id);
  }

  function isAnnouncementRead(id) {
    if (!Array.isArray(announcements.value) || !currentUserId.value) return false;
    const ann = announcements.value.find((a) => a?._id === id);
    if (!ann || !Array.isArray(ann.readBy)) return false;
    return ann.readBy.some(
      (r) => String(r?.userId) === String(currentUserId.value)
    );
  }

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /* ---------------- LIFECYCLE ---------------- */
  onMounted(async () => {
    store.dispatch("fetchAnnouncements");

    if (teachersList.value.length === 0) {
      store.dispatch("fetchTeachers", { page: 1, limit: 200 });
    }

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
    }
  });

  onUnmounted(() => {
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", handleKeyDown);
    }
  });
</script>

<style scoped>
  .list-enter-active,
  .list-leave-active {
    transition: all 0.2s ease;
  }
  .list-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }
  .list-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
</style>
